import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Phone, Mail, MapPin, Clock, MessageSquare, Loader2 } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Kontakt = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    objectSize: '',
    floor: '',
    elevator: false,
    areas: [],
    preferredDate: '',
    message: '',
    photos: null,
    consent: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      alert('Bitte bestätigen Sie die Datenschutzerklärung.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        submittedAt: new Date().toISOString()
      });
      
      if (response.status === 200 || response.status === 201) {
        setSubmitted(true);
        // Redirect to thank you page after short delay
        setTimeout(() => {
          window.location.href = '/danke';
        }, 1500);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Es gab einen Fehler beim Senden. Bitte versuchen Sie es erneut oder rufen Sie uns an.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAreaChange = (area) => {
    setFormData(prev => ({
      ...prev,
      areas: prev.areas.includes(area)
        ? prev.areas.filter(a => a !== area)
        : [...prev.areas, area]
    }));
  };

  return (
    <div className="bg-white py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Breadcrumb items={[{ label: 'Kontakt' }]} />
        
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h1 className="text-4xl font-bold text-[#0F172A] mb-6">Kontakt & schnelles Angebot</h1>
            <p className="text-lg text-gray-700 mb-8">
              Sie möchten ein Festpreis-Angebot für Ihre Haushaltsauflösung in Dortmund? 
              Kontaktieren Sie uns auf dem Weg, der Ihnen am besten passt.
            </p>

            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#16A34A] rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-1">Telefon (sofort)</h3>
                  <a href="tel:+4923139085116" className="text-[#16A34A] text-lg font-semibold hover:underline">
                    0231 39085116
                  </a>
                  <p className="text-sm text-gray-600 mt-1">Rückmeldung i.d.R. am selben Tag</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#16A34A] rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-1">WhatsApp</h3>
                  <a 
                    href="https://wa.me/4923139085116" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-[#16A34A] text-lg font-semibold hover:underline"
                  >
                    0231 39085116
                  </a>
                  <p className="text-sm text-gray-600 mt-1">Schnelle Antworten, auch per Foto/Video</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#16A34A] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-1">E-Mail</h3>
                  <a 
                    href="mailto:info@haushaltsaufloesung.services" 
                    className="text-[#16A34A] font-semibold hover:underline break-all"
                  >
                    info@haushaltsaufloesung.services
                  </a>
                  <p className="text-sm text-gray-600 mt-1">Für ausführliche Anfragen</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <MapPin className="h-6 w-6 text-[#16A34A] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-1">Adresse</h3>
                  <p className="text-gray-700">
                    Schössow Dienstleistungen<br />
                    Neben dem Brand 10<br />
                    44135 Dortmund<br />
                    Nordrhein-Westfalen, Deutschland
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="h-6 w-6 text-[#16A34A] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-1">Öffnungszeiten</h3>
                  <p className="text-gray-700">
                    Montag – Samstag: 08:00 – 20:00 Uhr<br />
                    <span className="text-sm text-gray-600">Anfragen jederzeit möglich</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Angebot anfordern</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefon *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="address">Adresse/Räum-Ort in Dortmund *</Label>
                  <Input
                    id="address"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="z.B. Musterstraße 10, 44135 Dortmund"
                    className="mt-1"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="objectSize">Objektgröße</Label>
                    <select
                      id="objectSize"
                      name="objectSize"
                      value={formData.objectSize}
                      onChange={handleChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="1-zimmer">1-Zimmer-Wohnung</option>
                      <option value="2-zimmer">2-Zimmer-Wohnung</option>
                      <option value="3-zimmer">3-Zimmer-Wohnung</option>
                      <option value="4-zimmer">4+ Zimmer</option>
                      <option value="einfamilienhaus">Einfamilienhaus</option>
                      <option value="mehrfamilienhaus">Mehrfamilienhaus</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="floor">Etage</Label>
                    <Input
                      id="floor"
                      name="floor"
                      value={formData.floor}
                      onChange={handleChange}
                      placeholder="z.B. 3. OG"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="elevator"
                    name="elevator"
                    checked={formData.elevator}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, elevator: checked }))}
                  />
                  <Label htmlFor="elevator" className="cursor-pointer">Aufzug vorhanden</Label>
                </div>

                <div>
                  <Label className="mb-2 block">Zu räumende Bereiche</Label>
                  <div className="space-y-2">
                    {['Wohnung', 'Keller', 'Dachboden', 'Garage'].map(area => (
                      <div key={area} className="flex items-center gap-2">
                        <Checkbox
                          id={area}
                          checked={formData.areas.includes(area)}
                          onCheckedChange={() => handleAreaChange(area)}
                        />
                        <Label htmlFor={area} className="cursor-pointer">{area}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="preferredDate">Wunschtermin (optional)</Label>
                  <Input
                    id="preferredDate"
                    name="preferredDate"
                    type="date"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Nachricht / Besonderheiten</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                    className="mt-1"
                  />
                </div>

                <div className="flex items-start gap-2">
                  <Checkbox
                    id="consent"
                    name="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                    required
                  />
                  <Label htmlFor="consent" className="cursor-pointer text-sm">
                    Ich habe die <a href="/datenschutz" className="text-[#16A34A] underline" target="_blank">Datenschutzerklärung</a> gelesen 
                    und stimme der Verarbeitung meiner Daten zu. *
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || submitted}
                  className="w-full bg-[#16A34A] hover:bg-[#16A34A]/90 text-white text-lg h-14"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : submitted ? (
                    'Erfolgreich gesendet!'
                  ) : (
                    'Jetzt Angebot anfordern'
                  )}
                </Button>

                <p className="text-xs text-gray-600 text-center">
                  * Pflichtfelder. Wir melden uns schnellstmöglich mit Ihrem Festpreis-Angebot.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Kontakt;